import { Play, Heart, Plus } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CutLetters, AddToPlaylistPanel } from "./MiniPlayer";
import { askForLogin } from "../lib/askLogin";
import { askForPremium } from "../lib/askPremium";

export default function VideoCard({ video, onLikeToggle, size = "md", queue, playlist = false }) {   // playlist: the list is one the member keeps (their Favorites), so Autoplay and Shuffle work in it
  const player = usePlayer();
  const { user } = useAuth();
  const [liked, setLiked] = useState(video.is_liked);
  const [plOpen, setPlOpen] = useState(false);              // the "Add to playlist" box for this song

  const toggleLike = async (e) => {
    e.stopPropagation(); e.preventDefault();
    if (!user) { toast.error("Sign in to save favorites"); return; }
    if (!user.is_premium && user.role !== "admin") { askForPremium("Favorites are a premium feature"); return; }
    try {
      if (liked) { await api.delete(`/favorites/${video.id}`); setLiked(false); }
      else { await api.post(`/favorites/${video.id}`); setLiked(true); toast.success("Added to favorites"); }
      onLikeToggle && onLikeToggle(video.id, !liked);
    } catch { /* interceptor shows upgrade modal on premium_required */ }
  };

  const openPlaylists = (e) => {
    e.stopPropagation(); e.preventDefault();
    if (!user) { askForLogin("playlist"); return; }
    setPlOpen(true);
  };

  const sizeCls = size === "sm" ? "w-40 sm:w-48" : size === "lg" ? "w-72 sm:w-80" : "w-56 sm:w-64";

  // Pass the current page's list through navigation state so the Watch page's
  // Play button can resume playback within that same list instead of falling
  // back to "this song + related".
  const linkState = Array.isArray(queue) && queue.length > 0 ? { sourceQueue: queue, playlist } : undefined;

  return (
    <>
    <Link to={`/watch/${video.id}`} state={linkState} data-testid={`video-card-${video.id}`}
      className={`group relative shrink-0 ${sizeCls} card-hover rounded-xl overflow-hidden bg-zinc-900/60 border border-white/8 block`}>
      <div className="relative aspect-video overflow-hidden">
        <img src={video.cover_url} alt={video.title} loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
        <div className="absolute inset-0 scrim-bottom" />
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {video.premium && (
            <span data-testid={`premium-tag-${video.id}`} className="steel-plate steel-pill steel-tag steel-tag--card steel-gold"><CutLetters text="Premium" /></span>
          )}
          <span className="text-[10px] font-mono-ui uppercase tracking-wider bg-black/70 text-cyan-300 px-1.5 py-0.5 rounded border border-cyan-400/30">HD</span>
          {video.age_locked && (
            <span data-testid={`age-tag-${video.id}`} title="Explicit: 18 and older" className="text-[10px] font-mono-ui uppercase tracking-wider bg-black/70 text-rose-300 px-1.5 py-0.5 rounded border border-rose-400/40">18+</span>
          )}
        </div>
        <button data-testid={`like-btn-${video.id}`} onClick={toggleLike}
          className="absolute top-2 right-2 w-8 h-8 rounded-full glass flex items-center justify-center hover:scale-110 transition-transform">
          <Heart className={`w-4 h-4 ${liked ? "fill-crimson stroke-crimson" : "text-white"}`} style={{ fill: liked ? "#FF2A5F" : "none", stroke: liked ? "#FF2A5F" : "white" }} />
        </button>
        <button data-testid={`add-pl-btn-${video.id}`} onClick={openPlaylists} aria-label="Add to playlist" title="Add to playlist"
          className="absolute bottom-2 left-2 w-8 h-8 rounded-full glass flex items-center justify-center hover:scale-110 transition-transform">
          <Plus className="w-4 h-4 text-white" />
        </button>
        <button data-testid={`play-btn-${video.id}`} onClick={(e) => {
          e.preventDefault(); e.stopPropagation();
          if (Array.isArray(queue) && queue.length > 0) {
            const idx = queue.findIndex((v) => v.id === video.id);
            if (idx >= 0) { player.playQueue(queue, idx, { playlist }); return; }
          }
          player.playOne(video);
        }}
          className="absolute bottom-2 right-2 w-11 h-11 rounded-full bg-cyan-400 text-black flex items-center justify-center opacity-0 group-hover:opacity-100 touch-show transition-opacity shadow-lg shadow-cyan-500/30">
          <Play className="w-5 h-5 ml-0.5" />
        </button>
      </div>
      <div className="p-3">
        <div className="font-display font-semibold text-sm truncate">{video.title}</div>
        <div className="text-xs text-zinc-400 truncate">{video.artist}</div>
        <div className="flex items-center gap-2 mt-1.5 text-[10px] text-zinc-500 font-mono-ui uppercase">
          <span>{(video.play_count || 0).toLocaleString()} plays</span>
          {video.language && <><span>•</span><span>{video.language}</span></>}
        </div>
      </div>
    </Link>
    {plOpen && user && (
      <AddToPlaylistPanel fixed song={video} many={!!(user.is_premium || user.role === "admin")} onClose={() => setPlOpen(false)} />
    )}
    </>
  );
}
